import { z } from "zod";

export type BusinessType =
	| "corporation"
	| "sole_proprietorship"
	| "partnership"
	| "one_person_corporation"
	| "cooperative"
	| "foreign_corporation_company"
	| "not_applicable";

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
	corporation: "Corporation",
	sole_proprietorship: "Sole Proprietorship",
	partnership: "Partnership",
	one_person_corporation: "One Person Corporation",
	cooperative: "Cooperative",
	foreign_corporation_company: "Foreign Corporation / Company",
	not_applicable: "Not Applicable",
};

export const BUSINESS_TYPE_OPTIONS = [
	{ value: "corporation" as const, label: "Corporation" },
	{ value: "sole_proprietorship" as const, label: "Sole Proprietorship" },
	{ value: "partnership" as const, label: "Partnership" },
	{ value: "one_person_corporation" as const, label: "One Person Corporation" },
	{ value: "cooperative" as const, label: "Cooperative" },
	{ value: "foreign_corporation_company" as const, label: "Foreign Corporation / Company" },
	{ value: "not_applicable" as const, label: "Not Applicable" },
];

export type TaxRegistration = "non_vat" | "vat" | "vat_exempt" | "zero_rated";

export const TAX_REGISTRATION_OPTIONS = [
	{ value: "non_vat" as const, label: "Non-VAT" },
	{ value: "vat" as const, label: "VAT" },
	{ value: "vat_exempt" as const, label: "VAT Exempt" },
	{ value: "zero_rated" as const, label: "Zero-rated" },
];

export const VAT_RATE_OPTIONS = [
	{ value: "0.00", label: "0%" },
	{ value: "1.20", label: "1.2%" },
	{ value: "1.80", label: "1.8%" },
	{ value: "2.00", label: "2%" },
];

export const REQUIRED_DOCUMENT_NAMES = [
	"Business Permit",
	"SEC Registration Certificate or DTI Registration",
	"BIR Form 2303",
	"Tax Clearance",
	"Certificate of Insurance",
	"Audited Financial Statement",
	"Company Profile",
	"Catalogue of Products and Services",
	"ISO Certificate (If Any)",
] as const;

export const bomSupplierRequiredDocumentSchema = z.object({
	id: z.number().optional().nullable(),
	document_name: z.string(),
	pdf_path: z.string().optional().nullable(),
	pdf_url: z.string().optional().nullable(),
	file: z.instanceof(File).optional().nullable(),
	remove_file: z.boolean().optional().nullable(),
	verified: z.boolean().optional().nullable(),
	risk_if_none: z.string().optional().nullable(),
});
export type BomSupplierRequiredDocument = z.infer<typeof bomSupplierRequiredDocumentSchema>;

export const bomSupplierBankDetailSchema = z.object({
	id: z.number().optional().nullable(),
	bank: z.string().optional().nullable(),
	account_name: z.string().optional().nullable(),
	account_number: z.string().optional().nullable(),
	type_of_account: z.string().optional().nullable(),
	bank_branch: z.string().optional().nullable(),
});
export type BomSupplierBankDetail = z.infer<typeof bomSupplierBankDetailSchema>;

export const bomSupplierInfoSchema = z.object({
	id: z.string().optional(),
	// arsenal_id: z.string().optional().nullable(),
	supplier_name: z.string().optional().nullable(),
	contact_person: z.string().optional().nullable(),
	position: z.string().optional().nullable(),
	email: z.string().optional().nullable(),
	landline_number: z.string().optional().nullable(),
	mobile_number: z.string().optional().nullable(),
	viber: z.string().optional().nullable(),
	viber_group: z.string().optional().nullable(),
	skype: z.string().optional().nullable(),
	address: z.string().optional().nullable(),
	tin: z.string().optional().nullable(),
	year_established: z.string().optional().nullable(),
	business_type: z
		.enum(["corporation", "sole_proprietorship", "partnership", "one_person_corporation", "cooperative", "foreign_corporation_company", "not_applicable"])
		.optional()
		.nullable(),
	tax_registration: z.enum(["non_vat", "vat", "vat_exempt", "zero_rated"]).optional().nullable(),
	vat_rate: z.union([z.string(), z.number()]).optional().nullable(),
	years_in_business: z.string().optional().nullable(),
	product_service_category: z.string().optional().nullable(),
	terms: z.string().optional().nullable(),
	website: z.string().optional().nullable(),
	company_proprietor: z.string().optional().nullable(),
	company_proprietor_position: z.string().optional().nullable(),
	bank_details: z.array(bomSupplierBankDetailSchema).optional().nullable(),
	required_documents: z.array(bomSupplierRequiredDocumentSchema).optional().nullable(),
	created_at: z.string().optional().nullable(),
	updated_at: z.string().optional().nullable(),
});
export type BomSupplierInfo = z.infer<typeof bomSupplierInfoSchema>;

export const createBomSupplierInfoSchema = bomSupplierInfoSchema.omit({
	id: true,
	// arsenal_id: true,
	created_at: true,
	updated_at: true,
});
export type CreateBomSupplierInfoData = z.infer<typeof createBomSupplierInfoSchema>;

export const updateBomSupplierInfoSchema = createBomSupplierInfoSchema.partial();
export type UpdateBomSupplierInfoData = z.infer<typeof updateBomSupplierInfoSchema>;
